import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { collection, getDocs, doc, updateDoc, arrayUnion, getDoc } from "firebase/firestore";
import { db } from "../firebase";

export default function AddMembersToPennant({
  isOpen,
  toggle,
  pennant,
  refreshPennants,
}) {
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [existingMembers, setExistingMembers] = useState(
    pennant.members || {}
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  // Fetch all members from Firestore
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const membersCollection = collection(db, "members");
        const membersSnapshot = await getDocs(membersCollection);
        const membersList = membersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMembers(membersList);
      } catch (error) {
        console.error("Error fetching members: ", error);
      }
    };
  
    fetchMembers();
  }, []);
  

  // Get a list of all assigned member IDs
  const getAssignedMemberIds = () => Object.values(existingMembers).flat();

  // Filter and sort members
  const filteredAndSortedMembers = members
  .filter(
    (member) =>
      !getAssignedMemberIds().includes(member.id) &&
      (`${member.firstName} ${member.lastName}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
        member.email?.toLowerCase().includes(searchQuery.toLowerCase()))
  )
  .sort((a, b) => {
    const rankA = a.rankings?.[pennant.name] || Infinity; // Use pennant name as key
    const rankB = b.rankings?.[pennant.name] || Infinity;
    return sortOrder === "asc" ? rankA - rankB : rankB - rankA;
  });



  // Handle selecting/deselecting members
  const handleSelectMember = (memberId) => {
    setSelectedMembers((prevSelected) =>
      prevSelected.includes(memberId)
        ? prevSelected.filter((id) => id !== memberId)
        : [...prevSelected, memberId]
    );
  };


const handleAssignMembers = async (section) => {
  const updatedSectionMembers = [
    ...(Array.isArray(existingMembers[section]) ? existingMembers[section] : []),
    ...selectedMembers,
  ];

  const updatedMembers = {
    ...existingMembers,
    [section]: updatedSectionMembers,
  };

  try {
    const pennantDoc = doc(db, "Pennants", pennant.id);
    await updateDoc(pennantDoc, { members: updatedMembers });

    await Promise.all(
      selectedMembers.map(async (memberId) => {
        const memberDoc = doc(db, "members", memberId);
        await updateDoc(memberDoc, {
          pennants: arrayUnion({
            pennantId: pennant.id,
            section,
            name: `${pennant.sport} ${pennant.name}`,
          }),
        });
      })
    );


    setExistingMembers(updatedMembers);
    setSelectedMembers([]);
    refreshPennants();
  } catch (error) {
    console.error("❌ Error assigning members:", error);
  }
};

// ✅ Remove a member from a section and update their profile
const handleRemoveMember = async (section, memberId) => {
  const updatedSectionMembers = (Array.isArray(existingMembers[section])
    ? existingMembers[section]
    : []
  ).filter((id) => id !== memberId);

  const updatedMembers = {
    ...existingMembers,
    [section]: updatedSectionMembers,
  };

  try {
    const pennantDoc = doc(db, "Pennants", pennant.id);
    await updateDoc(pennantDoc, { members: updatedMembers });

    const memberDoc = doc(db, "members", memberId);
    const memberSnapshot = await getDoc(memberDoc);

    if (memberSnapshot.exists()) {
      const memberData = memberSnapshot.data();

      const updatedPennants = (memberData.pennants || []).filter(
        (pennantEntry) => pennantEntry.pennantId !== pennant.id
      );

      await updateDoc(memberDoc, {
        pennants: updatedPennants,
      });

    }

    setExistingMembers(updatedMembers);
    refreshPennants();
  } catch (error) {
    console.error("Error removing member:", error);
  }
};

  

  // Get unassigned members
  const unassignedMembers = members.filter(
    (member) => !getAssignedMemberIds().includes(member.id)
  );

  return (
    <Dialog open={isOpen} onClose={toggle} maxWidth="lg" fullWidth>
      <Container>
        <Typography variant="h4" gutterBottom>
          Manage Members for {pennant.name}
        </Typography>
        <Box display="flex" justifyContent="space-between" sx={{ mt: 3 }}>
          {/* All Members List */}
          <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">All Members</Typography>
            <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Search Members"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <FormControl fullWidth>
                <InputLabel id="sort-order-label">Sort by Rank</InputLabel>
                <Select
                  labelId="sort-order-label"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                >
                  <MenuItem value="asc">Lowest to Highest</MenuItem>
                  <MenuItem value="desc">Highest to Lowest</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <List>
  {filteredAndSortedMembers.map((member) => {
    const currentRank = member.rankings?.[pennant.name] || "N/A"; // Use the pennant name as key
    return (
      <ListItem key={member.id} button>
        <Checkbox
          checked={selectedMembers.includes(member.id)}
          onChange={() => handleSelectMember(member.id)}
        />
        <ListItemText
          primary={`${member.firstName} ${member.lastName}`}
          secondary={`Rank: ${currentRank}`}
        />
      </ListItem>
    );
  })}
</List>


          </Paper>

          {/* Sections and Assigned Members */}
          <Paper elevation={3} sx={{ flex: 2, ml: 2 }}>
            <Typography variant="h6" sx={{ p: 2 }}>
              Sections
            </Typography>
            {Object.keys(existingMembers).map((section) => (
              <Box key={section} sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle1" gutterBottom>
                    {section}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={selectedMembers.length === 0}
                    onClick={() => handleAssignMembers(section)}
                  >
                    Assign to {section}
                  </Button>
                </Box>
                <List>
                  {(Array.isArray(existingMembers[section])
                    ? existingMembers[section]
                    : []
                  ).map((memberId) => {
                    const member = members.find((m) => m.id === memberId);
                    const sectionRank = member?.rankings?.[pennant.name] || "N/A"; // Fetch rank for sections
                    return (
                      member && (
                        <ListItem key={memberId}>
                          <ListItemText
                            primary={`${member.firstName} ${member.lastName}`}
                            secondary={`Pennant Rank: ${sectionRank}`}
                          />
                          <ListItemSecondaryAction>
                            <Button
                              color="error"
                              onClick={() =>
                                handleRemoveMember(section, memberId)
                              }
                            >
                              Remove
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                      )
                    );
                  })}
                </List>
              </Box>
            ))}
          </Paper>
        </Box>
        <Button variant="outlined" onClick={toggle} sx={{ mt: 3 }}>
          Close
        </Button>
      </Container>
    </Dialog>
  );
}

AddMembersToPennant.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  pennant: PropTypes.object.isRequired,
  refreshPennants: PropTypes.func.isRequired,
};
